// Progressive Image Controller
// Logo skeleton background + direct image loading

import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"

const log = createLogger('ProgressiveImage')

export default class extends Controller {
  static targets = ["full"]

  connect() {
    this.isLoaded = false

    if (!this.hasFullTarget) {
      log.warn('No full target found')
      return
    }

    const fullImg = this.fullTarget

    // Already loaded (browser cache hit)
    if (fullImg.complete && fullImg.naturalHeight > 0) {
      this.handleLoadSuccess()
      return
    }

    this.setupLoadListeners(fullImg)
  }

  disconnect() {
    if (this.loadAbortController) {
      this.loadAbortController.abort()
      this.loadAbortController = null
    }
  }

  setupLoadListeners(img) {
    this.loadAbortController = new AbortController()
    const { signal } = this.loadAbortController

    img.addEventListener('load', () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        this.handleLoadSuccess()
      } else {
        log.warn('Image loaded but has zero dimensions')
        this.handleLoadError()
      }
    }, { once: true, signal })

    img.addEventListener('error', () => {
      log.warn('Image load error', { src: img.src })
      this.handleLoadError()
    }, { once: true, signal })
  }

  handleLoadSuccess() {
    this.isLoaded = true
    this.cleanupListeners()
    this.dispatchImageReady()
  }

  handleLoadError() {
    this.cleanupListeners()
    this.showErrorState()
    this.dispatchImageReady()
  }

  cleanupListeners() {
    if (this.loadAbortController) {
      this.loadAbortController.abort()
      this.loadAbortController = null
    }
  }

  dispatchImageReady() {
    this.element.dispatchEvent(new CustomEvent('image:ready', {
      bubbles: true,
      detail: { loaded: this.isLoaded }
    }))
  }

  showErrorState() {
    if (!this.hasFullTarget) return

    const fullTarget = this.fullTarget
    if (fullTarget.parentElement?.querySelector('[data-error-icon]')) return

    fullTarget.style.display = 'none'

    const errorIcon = document.createElement('div')
    errorIcon.setAttribute('data-error-icon', 'true')
    errorIcon.className = 'absolute inset-0 flex items-center justify-center bg-crumb-100'
    errorIcon.innerHTML = `
      <svg class="w-8 h-8 text-crumb-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    `
    fullTarget.parentElement.appendChild(errorIcon)
  }
}
